/**
 * Enhance (KMMO Forge › Enhance, mockup docs/art/mockups/enhance.jpg): one item, one button.
 *   centre  the item node on a gold ring, 2 material nodes (Fangsteel + the Scale actually paid),
 *           Success Rate (+ the "Blessing +15%" chip), Resonance as fails-to-guarantee,
 *           "+4 → +5" with up to 3 stat diffs, Auto-enhance + ENHANCE (gold cost / FREE) + ⋯
 *   right   Equipped | Bag slots to pick the target
 * At the tier cap the same button runs Masterwork; at the ceiling it reads MAX. Drill, Refine,
 * Reshape and Reforge live in the ⋯ Options sheet. Replaces the Temper screen (dial dropped).
 *
 * `renderEnhance(ctx, host, params)` draws into any positioned host (the Forge rail embeds it);
 * `showEnhanceInfo` is the (i) sheet text for it.
 */
import '../kmmo/kmmo.css';
import './enhance.css';
import type { ClassId } from '@keystone/sim';
import {
  type ForgeRoll,
  type Item,
  type OpError,
  type SecondaryStat,
  drillMaxSockets,
  drillOdds,
  equippedGear,
  ITEM_SLOTS,
  itemById,
  refineCost,
  shortfall,
  sigilReshapeOdds,
  temperOdds,
} from '@keystone/progression';
import type { IntentResult } from '../intents';
import type { MetaScreenContext } from '../context';
import type { RouteParams } from '../router';
import { el, svg } from '../ui/dom';
import { STAT_LABEL, itemName } from '../ui/itemText';
import {
  type ItemSlotHandle,
  attachHoverCard,
  gearCard,
  ghostButton,
  hideHoverCard,
  iconButton,
  iconTabs,
  iconUrl,
  itemSlot,
  kmModal,
  kmToast,
  panel,
  percentBadge,
  primaryButton,
  progressBar,
  secondaryButton,
  slotIcon,
  statDiff,
} from '../kmmo';
import { infoSheet } from '../kmmo/extra-hero';
import { bundleText, shortfallText } from '../forge/resourceText';
import { formatPct } from './forgeModel';
import { enhanceLevel, bagGear } from './heroModel';
import { enhanceDiffs, enhancePreview, enhanceView, outcomeText, temperPanel } from './temperModel';
import { REFORGE_PRICE, opErrorText, reforgeBlocked, reforgeChoices } from './upgradeModel';

/** Session choice: apply a Blessed Charge when it helps (tap the chip to turn it off). */
let blessOn = true;

type Side = 'equipped' | 'bag';

export function renderEnhance(ctx: MetaScreenContext, host: HTMLElement, params: RouteParams = {}): { dispose(): void; current(): string | null } {
  const store = ctx.store;
  const main = (): ClassId => store.profile.mainClassId;
  let side: Side = 'equipped';
  let itemId: string | null = typeof params['itemId'] === 'string' ? (params['itemId'] as string) : null;
  const root = el('div', 'ke-root', host);
  const center = el('div', 'ke-center', root);
  const sideP = panel({ className: 'ke-side' }, root);
  const handles = new Map<string, ItemSlotHandle>();

  const equipped = (): Item[] => {
    const g = equippedGear(store.profile, main());
    return ITEM_SLOTS.map((s) => g[s]).filter((i): i is Item => !!i);
  };

  function target(): Item | null {
    const p = store.profile;
    const it = itemId ? itemById(p, itemId) : undefined;
    if (it) return it;
    const eq = equipped();
    const pick = eq.find((i) => i.slot === 'weapon') ?? eq[0] ?? null;
    itemId = pick?.id ?? null;
    return pick;
  }

  function render(): void {
    hideHoverCard();
    const item = target();
    if (item && !equipped().some((i) => i.id === item.id) && side === 'equipped' && itemId === params['itemId']) side = 'bag';
    renderCenter(item);
    renderSide();
  }

  // ------------------------------------------------------------------ centre
  function renderCenter(item: Item | null): void {
    center.replaceChildren();
    if (!item) {
      el('div', 'ke-empty', center, 'Equip an item to enhance it.');
      return;
    }
    const p = store.profile;
    const v = enhanceView(p, item, blessOn);
    const title = el('div', 'ke-title', center);
    el('span', `ke-name km-r-${item.rarity}`, title, `${itemName(item)}${v.level ? ` +${v.level}` : ''}`);
    if (v.mode !== 'temper') el('span', 'ke-tag', title, v.mode === 'max' ? 'Max' : 'Masterwork');

    // Node graph: ring + item + materials.
    const graph = el('div', 'ke-graph', center);
    el('i', 'ke-ring ke-ring-1', graph);
    el('i', 'ke-ring ke-ring-2', graph);
    for (const k of ['n', 'e', 's', 'w']) el('i', `ke-ring-pip ke-pip-${k}`, graph);
    const lines = el('div', 'ke-lines', graph);
    lines.innerHTML = '<svg viewBox="0 0 760 530" preserveAspectRatio="none"><path d="M380 230 L190 400 M380 230 L570 400"/></svg>';
    const node = el('div', 'ke-item-node', graph);
    const h = itemSlot({ rarity: item.rarity, icon: slotIcon(item.slot), plus: v.level, setPiece: !!item.setId, size: 'xl', selected: true }, node);
    attachHoverCard(h.slot, () => gearCard(item));
    const mats = v.materials.length ? v.materials : [];
    mats.slice(0, 2).forEach((m, i) => {
      const w = el('div', `ke-mat ke-mat-${i === 0 ? 'l' : 'r'}`, graph);
      itemSlot({ rarity: m.key === 'fangsteel' ? 'rare' : m.key === 'scale.jade' ? 'uncommon' : m.key === 'scale.gilded' ? 'legendary' : m.key === 'scale.eternal' ? 'epic' : 'common', icon: iconUrl(m.icon, true), size: 'lg', caption: m.label, caption2: v.free ? 'Free' : `${m.have}/${m.need}`, captionTone: v.free ? 'gold' : m.have >= m.need ? 'good' : 'bad' }, w);
    });

    // Odds
    const odds = el('div', 'ke-odds', center);
    const pb = percentBadge(v.chance / 100, { note: v.mode === 'max' ? '' : v.failNote }, odds);
    if (v.mode === 'max') pb.el.classList.add('ke-hide');
    if (v.canBless) {
      const chip = el('button', `ke-bless${blessOn ? ' on' : ''}`, odds);
      (chip as HTMLButtonElement).type = 'button';
      chip.appendChild(Object.assign(document.createElement('img'), { src: iconUrl('forge_blessed', true), alt: '', className: 'ke-bless-ico' }));
      el('span', '', chip, `Blessing +${blessOn ? v.blessedPP : 15}%`);
      chip.title = blessOn ? `Uses 1 Blessed Charge (you have ${p.wallet.blessed}). Tap to turn off.` : 'Tap to use a Blessed Charge';
      chip.addEventListener('click', () => {
        blessOn = !blessOn;
        render();
      });
    }
    if (v.mode !== 'max') progressBar({ value: v.resonance.steps, max: v.resonance.max, tone: 'green', label: 'Resonance', value_text: 'fraction' }, center).el.classList.add('ke-res');

    // Diffs
    if (v.mode !== 'max') {
      const after = enhancePreview(item, v);
      const rows = enhanceDiffs(p, ctx.content.class(main()) ?? null, item, after);
      statDiff(rows, { head: [`+${v.level}`, `+${v.level + 1}`] }, center).classList.add('ke-diff');
    } else {
      el('div', 'ke-maxed', center, 'This item is fully enhanced for your rank.');
    }

    // Actions
    const act = el('div', 'ke-actions', center);
    secondaryButton('Auto-enhance', { disabled: !!v.blocked, title: v.blocked ?? 'Up to 5 tries; stops on a success', onClick: () => roll(item.id, true) }, act);
    primaryButton(v.mode === 'max' ? 'MAX' : 'Enhance', {
      size: 'lg',
      cost: v.mode === 'max' || v.free ? undefined : { amount: v.gold },
      sub: v.free ? 'Free' : undefined,
      disabled: !!v.blocked,
      title: v.blocked && v.blocked !== 'MAX' ? v.blocked : '',
      onClick: () => roll(item.id, false),
      className: 'ke-go',
    }, act);
    iconButton(MORE_ICON(), { framed: true, title: 'Options: Drill, Refine, Reshape, Reforge', onClick: () => openOptions(item.id) }, act).classList.add('ke-more');
    if (v.blocked && v.blocked !== 'MAX') el('div', 'ke-reason', center, v.blocked);
  }

  // ------------------------------------------------------------------ right: pick the item
  function renderSide(): void {
    sideP.body.replaceChildren();
    handles.clear();
    iconTabs([{ id: 'equipped', label: 'Equipped' }, { id: 'bag', label: 'Bag' }], { active: side, style: 'text', onChange: (id) => { side = id as Side; renderSide(); } }, sideP.body);
    const grid = el('div', 'ke-grid', sideP.body);
    const list = side === 'equipped' ? equipped() : bagGear(store.profile, main());
    for (const it of list) {
      const h = itemSlot({
        rarity: it.rarity,
        icon: slotIcon(it.slot),
        plus: enhanceLevel(it),
        setPiece: !!it.setId,
        equipped: side === 'equipped',
        equippedCorner: 'tl',
        selected: it.id === itemId,
        onClick: () => {
          itemId = it.id;
          for (const [k, x] of handles) x.setSelected(k === itemId);
          renderCenter(it);
        },
      }, grid);
      handles.set(it.id, h);
      attachHoverCard(h.slot, () => gearCard(it));
    }
    for (let i = list.length; i < 12 || i % 3; i++) itemSlot({ empty: true }, grid);
  }

  // ------------------------------------------------------------------ rolls
  /** A roll is with the server (online): ignore presses until it answers. */
  let rolling = false;

  // Rolls run through the intent table: in the browser offline, on the server (its RNG) online.
  function roll(id: string, many: boolean): void {
    if (rolling) return;
    const p = store.profile;
    const item = itemById(p, id);
    if (!item) return;
    const v = enhanceView(p, item, blessOn);
    if (v.blocked) return void kmToast(v.blocked === 'MAX' ? 'Already at its maximum' : v.blocked, 'error');
    rolling = true;
    const fail = (reason: string | null): void => void kmToast(reason ? opErrorText(reason as OpError) : 'Nothing to roll', 'error');
    const many$ = (r: IntentResult<'temperUntil'> | IntentResult<'masterworkUntil'>): void => {
      rolling = false;
      if (!r.ok) return fail(r.reason);
      if (!r.rolls.length) return fail(r.blocked);
      announce(r.rolls, id);
    };
    const one$ = (r: IntentResult<'temper'> | IntentResult<'masterwork'>): void => {
      rolling = false;
      if (!r.ok) return fail(r.reason);
      announce([r.roll], id);
    };
    if (v.mode === 'temper') {
      if (many) void store.act('temperUntil', { itemId: id, opts: v.opts, max: 5 }).then(many$);
      else void store.act('temper', { itemId: id, opts: v.opts }).then(one$);
    } else if (many) void store.act('masterworkUntil', { itemId: id, opts: v.opts, max: 5 }).then(many$);
    else void store.act('masterwork', { itemId: id, opts: v.opts }).then(one$);
  }

  function announce(rolls: readonly ForgeRoll[], id: string): void {
    const last = rolls[rolls.length - 1];
    if (!last) return;
    const after = itemById(store.profile, id);
    const node = center.querySelector('.ke-item-node');
    node?.classList.remove('ke-pulse', 'ke-shake');
    void (node as HTMLElement | null)?.offsetWidth;
    if (last.outcome === 'fail') {
      node?.classList.add('ke-shake');
      const o = outcomeText(last);
      kmToast(`Failed${rolls.length > 1 ? ` ×${rolls.length}` : ''} · ${o.resonanceLine ?? ''}${o.refundLine ? ' · refunded' : ''}`, 'error', 1600);
      return;
    }
    node?.classList.add('ke-pulse');
    const lvl = after ? enhanceLevel(after) : 0;
    const o = outcomeText(last);
    const tries = rolls.length > 1 ? ` (${rolls.length} tries)` : '';
    kmToast(last.outcome === 'surge' ? `SURGE! Enhanced to +${lvl}${tries}` : `Enhanced to +${lvl}${tries}`, 'success');
    if (o.awakeningLine) kmToast(o.awakeningLine.replace(/^AWAKENING: /, 'Awakening: '), 'success', 3200);
  }

  // ------------------------------------------------------------------ ⋯ options sheet
  function openOptions(id: string): void {
    let unsub = (): void => {};
    const m = kmModal('Options', { wide: true, onClose: () => unsub() });
    const draw = (): void => {
      m.body.replaceChildren();
      const item = itemById(store.profile, id);
      if (!item) return m.close();
      optionCards(item, m.body);
    };
    draw();
    unsub = store.subscribe(draw);
  }

  function optionCards(item: Item, body: HTMLElement): void {
    const p = store.profile;
    const card = (title: string, line: string): { el: HTMLElement; foot: HTMLElement } => {
      const c = el('div', 'ke-opt', body);
      el('div', 'ke-opt-title', c, title);
      el('div', 'ke-opt-line', c, line);
      return { el: c, foot: el('div', 'ke-opt-foot', c) };
    };
    const apply = (run: () => Promise<{ ok: boolean; reason?: string }>, ok?: string): void => {
      void run().then((r) => {
        if (!r.ok) kmToast(opErrorText(r.reason as OpError), 'error');
        else if (ok) kmToast(ok, 'success');
      });
    };
    const rollSimple = (run: () => Promise<IntentResult<'drill'>>, what: string): void => {
      void run().then((r) => {
        if (!r.ok) return void kmToast(opErrorText(r.reason as OpError), 'error');
        kmToast(r.roll.outcome === 'fail' ? `${what} failed · Resonance +${r.roll.resonanceAfter - r.roll.resonanceBefore}` : `${what} succeeded`, r.roll.outcome === 'fail' ? 'error' : 'success');
      });
    };

    // Drill
    const maxS = drillMaxSockets(item);
    if (item.sockets < maxS) {
      const d = drillOdds(p, item, {});
      const c = card(`Drill a socket · ${formatPct(d.odds.chance)}`, `Sockets ${item.sockets}/${maxS} · ${bundleText(d.cost, 'free')}`);
      secondaryButton('Drill', { size: 'sm', disabled: !!d.blocked, title: d.blocked ? (shortfallText(d.shortfall) ?? '') : '', onClick: () => rollSimple(() => store.act('drill', { itemId: item.id }), 'Drill') }, c.foot);
    }

    // Refine / pending keep-or-discard (also the Reshape result)
    const pr = p.forge.pendingRefine;
    if (pr && pr.itemId === item.id) {
      const lines = item.secondaries.map((s, i) => {
        const to = pr.stats?.[i] ?? s.stat;
        const was = Math.round((s.quality ?? 1) * 100);
        const now = Math.round((pr.qualities[i] ?? 1) * 100);
        return to === s.stat ? `${STAT_LABEL[s.stat]} ${was}% → ${now}%` : `${STAT_LABEL[s.stat]} → ${STAT_LABEL[to]} ${now}%`;
      });
      const c = card('New stats rolled', lines.join(' · '));
      secondaryButton('Keep old', { size: 'sm', onClick: () => apply(() => store.act('discardRefine', {}), 'Kept the old stats') }, c.foot);
      primaryButton('Keep new', { size: 'sm', onClick: () => apply(() => store.act('keepRefine', {}), 'New stats applied') }, c.foot);
    } else if (pr) {
      card('Refine', 'Another item has new stats waiting. Keep or discard them there first.');
    } else {
      const cost = refineCost(item, 1);
      const short = shortfallText(shortfall(p, cost));
      const c = card('Refine', `Reroll stat quality (90–110%) · ${bundleText(cost, 'free')}`);
      secondaryButton('Refine', { size: 'sm', disabled: !!short, title: short ?? '', onClick: () => apply(() => store.act('refine', { itemId: item.id })) }, c.foot);
      if (item.setId) {
        const o = sigilReshapeOdds(p, item);
        const c2 = card('Reshape', `Reroll this set piece’s stats · ${bundleText(o.cost, 'free')}`);
        secondaryButton('Reshape', { size: 'sm', disabled: !!o.blocked, title: o.blocked ? opErrorText(o.blocked as Parameters<typeof opErrorText>[0]) : '', onClick: () => apply(() => store.act('reshape', { itemId: item.id })) }, c2.foot);
      }
    }

    // Reforge
    const blocked = reforgeBlocked(p, item);
    if (item.reforged) {
      const c = card('Reforge', 'Reforged. Revert it for free to choose again.');
      secondaryButton('Revert', { size: 'sm', onClick: () => apply(() => store.act('revertReforge', { itemId: item.id }), 'Reforge reverted') }, c.foot);
    } else if (blocked) {
      card('Reforge', blocked);
    } else {
      const c = card('Reforge', `Swap one stat for another · ${bundleText(REFORGE_PRICE, 'free')}`);
      item.secondaries.forEach((s, i) => {
        const row = el('div', 'ke-opt-row', c.el);
        el('span', 'ke-opt-from', row, STAT_LABEL[s.stat]);
        for (const to of reforgeChoices(item, i) as SecondaryStat[]) ghostButton(`→ ${STAT_LABEL[to]}`, { size: 'sm', onClick: () => apply(() => store.act('reforge', { itemId: item.id, index: i, to }), `Reforged to ${STAT_LABEL[to]}`) }, row);
      });
      c.foot.remove();
    }
  }

  render();
  const unsub = store.subscribe(() => render());
  return {
    dispose() {
      unsub();
      hideHoverCard();
      root.remove();
    },
    current: () => itemId,
  };
}

const MORE_ICON = (): SVGSVGElement => svg('<svg class="km-ico" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5.5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="18.5" cy="12" r="1.8"/></svg>');

/** Explanations for the (i). */
export function showEnhanceInfo(ctx: MetaScreenContext, itemId?: string | null): void {
  const p = ctx.store.profile;
  const item = itemId ? itemById(p, itemId) : null;
  const rows: [string, string][] = [];
  if (item) {
    const t = temperOdds(p, item, { useFree: true });
    if (t.blocked !== 'capped') temperPanel(t).byAttempt.forEach((x, i) => rows.push([`Success by try ${i + 1}`, x]));
  }
  infoSheet('Enhance', [
    'Each success raises the item by +1: 5 item levels (a Surge gives two steps). A failure never breaks the item: it refunds half the materials, adds +10% to the next try and fills Resonance. Full Resonance guarantees the next try.',
    'Every 5th step Awakens the item: an empowered stat, a new socket, a refund or a rarity upgrade.',
    'At your rank’s item level cap, Enhance becomes Masterwork (+1 item level per step, up to the rarity’s ceiling). Climb a rank to raise the cap.',
    'A Blessed Charge adds +15% when the chance is below 100%. Tap the chip to save your charges.',
  ], rows);
}
